import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getDropdownValues } from '../../services/moduleDataApi';
import { getFieldType } from './fieldTypeRegistry.jsx';

const SEARCH_DEBOUNCE_MS = 300;

function normalizeOptions(options = []) {
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (option === null || option === undefined) return null;
      if (typeof option === 'string' || typeof option === 'number') {
        return { label: String(option), value: option };
      }
      const label = option.label ?? option.name ?? option.value ?? option.id;
      const value = option.value ?? option.id ?? option.name ?? option.label;
      if (value === undefined) return null;
      // Preserved (not just label/value) so checkbox/radio can pre-select and
      // disable it — a remote source could plausibly carry this too, even
      // though today only the static admin-authored list does.
      return { label: String(label ?? value), value, locked: Boolean(option.locked) };
    })
    .filter(Boolean);
}

// Resolves the options for any registry type flagged needsOptions — from the
// generic dataSource endpoint, or from the type's own fetchOptions — and re-runs
// that resolution as the user types when the type is server-searched, so
// individual renderers never touch the network.
export default function SearchFieldControl({
  field, moduleName, value, onChange, disabled, extraProps,
}) {
  const definition = getFieldType(field.type ?? 'text');
  const dataSource = field.dataSource ?? 'static';
  // A type may resolve its own options (location → the location master) so a
  // config that only says type:"location" still gets real values, exactly as it
  // does on the add form. Everything else goes through the generic dataSource
  // endpoint as before.
  const ownFetcher = typeof definition.fetchOptions === 'function' ? definition.fetchOptions : null;
  const isRemote = Boolean(definition.needsOptions) && (Boolean(ownFetcher) || dataSource !== 'static');

  const staticOptions = useMemo(
    () => normalizeOptions(field.options ?? field.values ?? []),
    [field.options, field.values],
  );
  // What the user has typed into a server-searched control. Debounced before it
  // reaches the fetch, so a world-wide list is not re-queried on every keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [remoteOptions, setRemoteOptions] = useState(null);
  // Seeded from isRemote rather than flipped inside the effect: a remote field
  // is loading from its first render, so there is nothing to announce later.
  const [optionsLoading, setOptionsLoading] = useState(isRemote);

  useEffect(() => {
    if (!definition.remoteSearch) return undefined;
    if (search === debouncedSearch) return undefined;
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch, definition.remoteSearch]);

  useEffect(() => {
    if (!isRemote) return undefined;

    let cancelled = false;

    (async () => {
      // Inside the async body, not the effect body: a refetch (the user typed
      // into a server-searched control) has to show the spinner again, and the
      // first render already seeded it from isRemote.
      setOptionsLoading(true);
      try {
        const raw = ownFetcher
          ? await ownFetcher({ field, search: debouncedSearch, moduleName })
          : await getDropdownValues(moduleName, field.field, '', 200, 0, {
            dataSource,
            masterName: field.masterName,
            groupName: field.groupName,
          });
        const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.values ?? []);
        if (!cancelled) setRemoteOptions(normalizeOptions(list));
      } catch (error) {
        console.error(`[SearchBuilderV1] Failed to load options for "${field.field}":`, error);
        if (!cancelled) setRemoteOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemote, ownFetcher, debouncedSearch, dataSource, field.field, field.masterName,
    field.groupName, field.limit, moduleName]);

  return definition.render({
    field,
    value,
    onChange: onChange ?? (() => {}),
    options: remoteOptions ?? staticOptions,
    optionsLoading,
    disabled,
    placeholder: field.placeholder,
    moduleName,
    // Only a server-searched type gets this: every other renderer keeps antd's
    // own local filtering and the prop it never asked for stays undefined.
    onSearch: definition.remoteSearch ? setSearch : undefined,
    // A type may need form-level context no other type does — the keywords
    // control needs the Boolean mode it draws the toggle for. Spread last so a
    // type that wants none is called with exactly the props it had before.
    ...(extraProps ?? {}),
  });
}

SearchFieldControl.propTypes = {
  field: PropTypes.object.isRequired,
  moduleName: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  // Per-type extras from the form, merged into the renderer props.
  extraProps: PropTypes.object,
};
